import { NativeGeocoder, Address } from "@capgo/nativegeocoder";
import axios from "axios";
import { Preferences } from "@capacitor/preferences";

// Environment variables should be properly configured in your application
const WEATHER_API_KEY =
  process.env.OPENWEATHER_API_KEY || "761a44166ae9886f2b9f90e63d0c0365";
const BASE_URL = "https://api.openweathermap.org/data/2.5";
const WEATHER_CACHE_KEY = "weather_data_cache";
const AIR_QUALITY_CACHE_KEY = "air_quality_data_cache";
const CACHE_EXPIRY = 60 * 60 * 1000; // 1 hour in milliseconds

export interface WeatherData {
  coord: Coord;
  weather: Weather[];
  base: string;
  main: Main;
  visibility: number;
  wind: Wind;
  clouds: Clouds;
  dt: number;
  sys: Sys;
  timezone: number;
  id: number;
  name: string;
  cod: number;
}

interface Sys {
  country: string;
  sunrise: number;
  sunset: number;
}

interface Clouds {
  all: number;
}

interface Wind {
  speed: number;
  deg: number;
  gust: number;
}

interface Main {
  temp: number;
  feels_like: number;
  temp_min: number;
  temp_max: number;
  pressure: number;
  humidity: number;
  sea_level: number;
  grnd_level: number;
}

interface Weather {
  id: number;
  main: string;
  description: string;
  icon: string;
}

interface Coord {
  lon: number;
  lat: number;
}
export interface AirQualityData {
  aqi: number; // Air Quality Index
  co: number; // Carbon Monoxide
  no2: number; // Nitrogen Dioxide
  o3: number; // Ozone
  pm2_5: number; // PM2.5
  pm10: number; // PM10
  location: string;
}

/**
 * Fetch weather data for a given location, with caching
 * @param lat Latitude
 * @param lon Longitude
 * @returns Promise with weather data
 */
export async function getWeatherData(
  lat: number,
  lon: number
): Promise<WeatherData> {
  try {
    // Check if we have cached data first
    const cachedData = await getCachedData<WeatherData>(
      WEATHER_CACHE_KEY,
      lat,
      lon
    );
    if (cachedData) {
      return cachedData;
    }

    // If no cached data, fetch from API
    const response = await axios.get(`${BASE_URL}/weather`, {
      params: {
        lat,
        lon,
        units: "metric",
        appid: WEATHER_API_KEY,
      },
    });

    // Cache the result
    await cacheData(WEATHER_CACHE_KEY, lat, lon, response.data);

    return response.data;
  } catch (error) {
    console.error("Error fetching weather data:", error);
    throw new Error("Failed to fetch weather data");
  }
}

/**
 * Fetch air quality data for a given location, with caching
 * @param lat Latitude
 * @param lon Longitude
 * @returns Promise with air quality data
 */
export async function getAirQualityData(
  lat: number,
  lon: number
): Promise<AirQualityData> {
  try {
    // Check if we have cached data first
    const cachedData = await getCachedData<AirQualityData>(
      AIR_QUALITY_CACHE_KEY,
      lat,
      lon
    );
    if (cachedData) {
      return cachedData;
    }

    // If no cached data, fetch from API
    const response = await axios.get(`${BASE_URL}/air_pollution`, {
      params: {
        lat,
        lon,
        appid: WEATHER_API_KEY,
      },
    });

    const data = response.data.list[0].components;

    const airQualityData = {
      aqi: response.data.list[0].main.aqi,
      co: data.co,
      no2: data.no2,
      o3: data.o3,
      pm2_5: data.pm2_5,
      pm10: data.pm10,
      location: `${lat},${lon}`,
    };

    // Cache the result
    await cacheData(AIR_QUALITY_CACHE_KEY, lat, lon, airQualityData);

    return airQualityData;
  } catch (error) {
    console.error("Error fetching air quality data:", error);
    throw new Error("Failed to fetch air quality data");
  }
}

/**
 * Helper function to check if there's valid cached data
 */
async function getCachedData<T>(
  key: string,
  lat: number,
  lon: number
): Promise<T | null> {
  try {
    const cacheKey = `${key}_${lat.toFixed(4)}_${lon.toFixed(4)}`;
    const { value } = await Preferences.get({ key: cacheKey });

    if (value) {
      const storedData = JSON.parse(value);
      const now = Date.now();

      // Check if data is still valid (less than 1 hour old)
      if (storedData.timestamp && now - storedData.timestamp < CACHE_EXPIRY) {
        return storedData.data;
      }
    }
    return null;
  } catch (error) {
    console.error("Error reading cache:", error);
    return null;
  }
}

/**
 * Helper function to cache data with timestamp
 */
async function cacheData<T>(
  key: string,
  lat: number,
  lon: number,
  data: T
): Promise<void> {
  try {
    const cacheKey = `${key}_${lat.toFixed(4)}_${lon.toFixed(4)}`;
    const cacheObject = {
      timestamp: Date.now(),
      data,
    };
    await Preferences.set({
      key: cacheKey,
      value: JSON.stringify(cacheObject),
    });
  } catch (error) {
    console.error("Error caching data:", error);
  }
}

/**
 * Get both weather and air quality data for a location
 * @param lat Latitude
 * @param lon Longitude
 * @returns Promise with both weather and air quality data
 */
export async function getEnvironmentalData(
  lat: number,
  lon: number
): Promise<{ weather: WeatherData; airQuality: AirQualityData }> {
  const [weather, airQuality] = await Promise.all([
    getWeatherData(lat, lon),
    getAirQualityData(lat, lon),
  ]);

  return { weather, airQuality };
}

/**
 * Get address information from coordinates using the NativeGeocoder
 * @param latitude Latitude coordinate
 * @param longitude Longitude coordinate
 * @returns Promise with geocoding results
 */
export const getAddressFromGeoCoder = async (
  latitude: number,
  longitude: number
): Promise<Address[]> => {
  // Validate input parameters
  if (typeof latitude !== "number" || isNaN(latitude)) {
    throw new Error("Invalid latitude: must be a valid number");
  }
  if (typeof longitude !== "number" || isNaN(longitude)) {
    throw new Error("Invalid longitude: must be a valid number");
  }

  try {
    // Use the proper API format for @capgo/nativegeocoder
    const result = await NativeGeocoder.reverseGeocode({
      latitude,
      longitude,
      useLocale: true, // Use device locale for formatting
    });

    // Return just the addresses array for compatibility with existing code
    return result.addresses;
  } catch (error) {
    console.error("Error in geocoding:", error);
    throw new Error("Failed to get address from coordinates");
  }
};

export default {
  getWeatherData,
  getAirQualityData,
  getEnvironmentalData,
  getAddressFromGeoCoder,
};
